/**
 * Push Notifications Service
 * 
 * Handles FCM (Firebase Cloud Messaging) push notifications on native platforms.
 * Uses @capacitor/push-notifications for iOS/Android.
 * 
 * Features:
 * - Request permission
 * - Register device token and save to Firestore
 * - Handle incoming notifications
 * - Handle notification tap actions
 * 
 * @author Heseda AI Team
 * @version 1.1
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';

// Callback types
type TokenCallback = (token: string) => void;
type NotificationCallback = (notification: PushNotificationSchema) => void;
type ActionCallback = (action: ActionPerformed) => void;

// Store callbacks
let onTokenReceived: TokenCallback | null = null;
let onNotificationReceived: NotificationCallback | null = null;
let onNotificationAction: ActionCallback | null = null;

/**
 * Save FCM token to Firestore for the current user
 * Stores in masterProfiles/{uid}.notifications.pushTokens array with device info
 * Deduplicates tokens by checking if token already exists before adding
 */
async function saveTokenToFirestore(token: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    console.log('No authenticated user, skipping token save');
    return;
  }

  try {
    const platform = Capacitor.getPlatform();
    
    // Phase 1 Step 4: read only from masterProfiles
    const profileDocRef = doc(db, 'masterProfiles', user.uid);
    const profileDoc = await getDoc(profileDocRef);
    const existingData = profileDoc.data();
    
    let pushTokens: Array<{ token: string; platform?: string; createdAt?: string }> = 
      (existingData?.notifications?.pushTokens || [])
        .map((t: unknown) =>
          typeof t === 'string' ? { token: t } : t as { token: string; platform?: string; createdAt?: string }
        )
        .filter((t: { token: string; platform?: string; createdAt?: string } | null): t is { token: string; platform?: string; createdAt?: string } =>
          t != null && typeof t.token === 'string' && t.token.length > 0
        );
    
    // Check if this exact token already exists
    const tokenExists = pushTokens.some((t) => t.token === token);
    
    if (tokenExists) {
      console.log('📱 FCM token already registered, skipping duplicate save');
      // Phase 1 Step 4: write only to masterProfiles (with normalized tokens)
      await setDoc(profileDocRef, {
        notifications: {
          pushTokens,
          pushEnabled: true,
          lastTokenUpdate: serverTimestamp(),
        },
      }, { merge: true });
      return;
    }
    
    // Remove any old tokens for the same platform (replace old with new)
    pushTokens = pushTokens.filter((t) => t.platform !== platform);
    
    // Add the new token
    pushTokens.push({
      token,
      platform,
      createdAt: new Date().toISOString(),
    });
    
    // Phase 1 Step 4: write only to masterProfiles
    await setDoc(profileDocRef, {
      notifications: {
        pushTokens,
        pushEnabled: true,
        lastTokenUpdate: serverTimestamp(),
      },
    }, { merge: true });

    console.log('📱 FCM token saved to Firestore for user:', user.uid, '(total tokens:', pushTokens.length, ')');
  } catch (error) {
    console.error('Failed to save FCM token to Firestore:', error);
  }
}

/**
 * Check if push notifications are supported on current platform
 */
export function isPushSupported(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Initialize push notifications
 * Call this early in app startup (e.g., App.tsx useEffect)
 */
export async function initPushNotifications(options: {
  onToken?: TokenCallback;
  onNotification?: NotificationCallback;
  onAction?: ActionCallback;
} = {}): Promise<boolean> {
  if (!isPushSupported()) {
    console.log('Push notifications not supported on web');
    return false;
  }

  // Store callbacks FIRST before any async operations
  onTokenReceived = options.onToken || null;
  onNotificationReceived = options.onNotification || null;
  onNotificationAction = options.onAction || null;

  try {
    // Brief delay to ensure Capacitor Bridge is fully initialized.
    // Prevents NPE in Bridge.getPermissionStates() on Android when
    // checkPermissions is called before the bridge's internal state is ready.
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Remove any existing listeners to avoid duplicates
    await PushNotifications.removeAllListeners();
    
    // Set up listeners BEFORE registering
    setupListeners();

    // Check current permission status
    const permStatus = await PushNotifications.checkPermissions();
    
    if (permStatus.receive === 'granted') {
      // Already granted - register to trigger token event
      // This will fire the 'registration' event which our listeners will catch
      console.log('📱 Permission granted, registering for push...');
      await PushNotifications.register();
      return true;
    }
    
    console.log('📱 Push permission not yet granted:', permStatus.receive);
    return false;
  } catch (error) {
    console.error('📱 Push init failed (Bridge may not be ready):', error);
    return false;
  }
}

/**
 * Request permission for push notifications
 * Should be called after user takes an action (not on app start)
 */
export async function requestPushPermission(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }

  try {
    const permStatus = await PushNotifications.requestPermissions();
    
    if (permStatus.receive === 'granted') {
      await PushNotifications.register();
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error requesting push permission:', error);
    return false;
  }
}

/**
 * Get current permission status
 */
export async function getPushPermissionStatus(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!isPushSupported()) {
    return 'denied';
  }

  try {
    const status = await PushNotifications.checkPermissions();
    // Map Capacitor's PermissionState to simplified type
    if (status.receive === 'granted') return 'granted';
    if (status.receive === 'denied') return 'denied';
    return 'prompt'; // includes 'prompt' and 'prompt-with-rationale'
  } catch (error) {
    console.error('📱 checkPermissions failed (Bridge may not be ready):', error);
    return 'prompt';
  }
}

/**
 * Set up push notification listeners
 */
function setupListeners(): void {
  console.log('📱 Setting up push notification listeners...');
  
  // On registration success
  PushNotifications.addListener('registration', async (token: Token) => {
    console.log('📱 Push registration success, token:', token.value.substring(0, 30) + '...');
    
    // Save token to Firestore for the current user
    await saveTokenToFirestore(token.value);
    
    // Subscribe to 'all_users' topic for broadcast notifications
    // Note: This is handled via Firebase Cloud Functions for web SDK,
    // but for native apps we can use the token directly for multicast
    console.log('📱 Token registered - ready for push notifications');
    
    if (onTokenReceived) {
      onTokenReceived(token.value);
    }
  });

  // On registration error
  PushNotifications.addListener('registrationError', (error: any) => {
    console.error('❌ Push registration error:', JSON.stringify(error));
  });

  // When notification is received while app is in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    console.log('📬 Push notification received:', notification);
    if (onNotificationReceived) {
      onNotificationReceived(notification);
    }
  });

  // When user taps on a notification
  PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    console.log('👆 Push notification action:', action);
    if (onNotificationAction) {
      onNotificationAction(action);
    }
  });
}

/**
 * Remove all listeners (call on cleanup/logout)
 */
export async function removePushListeners(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }
  
  await PushNotifications.removeAllListeners();
  onTokenReceived = null;
  onNotificationReceived = null;
  onNotificationAction = null;
}

/**
 * Get list of delivered notifications (Android only)
 */
export async function getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
  if (!isPushSupported()) {
    return [];
  }
  
  const result = await PushNotifications.getDeliveredNotifications();
  return result.notifications;
}

/**
 * Remove all delivered notifications
 */
export async function clearAllNotifications(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }
  
  await PushNotifications.removeAllDeliveredNotifications();
}
